import {
  SUPPORT_SUBMIT_PATH,
  type SubmitInquiryRequest,
  type SubmitInquiryResponse,
} from '@cottonray/support-contract'
import type { PreparedPhoto } from './photos'

/**
 * 접수 결과. **예외를 던지지 않는다** — 실패해도 사용자가 쓴 글은 화면에 그대로 남아야 한다.
 * (앱에 있던 `@cottonray/support-native` 의 `client.ts` 와 같은 판단이다.)
 */
export type SubmitResult = { ok: true; id: string } | SubmitFailure

export interface SubmitFailure {
  ok: false
  reason: 'rateLimited' | 'invalid' | 'tooLarge' | 'network' | 'server'
}

/** 사진이 붙으면 늘린다 — LTE 에서 몇 MB 를 15초에 올릴 수는 없다 */
const TIMEOUT_MS = { plain: 15_000, withPhotos: 60_000 }

export async function submitInquiry(
  request: SubmitInquiryRequest,
  photos: readonly PreparedPhoto[] = [],
): Promise<SubmitResult> {
  const controller = new AbortController()
  const timer = setTimeout(
    () => controller.abort(),
    photos.length > 0 ? TIMEOUT_MS.withPhotos : TIMEOUT_MS.plain,
  )

  try {
    const response = await fetch(SUPPORT_SUBMIT_PATH, {
      method: 'POST',
      // multipart 는 boundary 를 fetch 가 정해야 한다 — content-type 을 직접 넣으면
      // boundary 가 빠져 서버가 파트를 하나도 못 읽는다
      ...(photos.length > 0
        ? { body: multipartBody(request, photos) }
        : {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(prune(request)),
          }),
      signal: controller.signal,
    })

    if (response.ok) {
      const body = (await response.json()) as SubmitInquiryResponse
      return { ok: true, id: body.id }
    }
    if (response.status === 429) return { ok: false, reason: 'rateLimited' }
    // 413 은 **우리 서버가 아니라 nginx 가 냈을 수 있다** — 그때 본문은 JSON 이 아니라
    // nginx 의 HTML 이다. 상태 코드로 먼저 갈라야 하는 이유다
    if (response.status === 413) return { ok: false, reason: 'tooLarge' }
    if (response.status >= 400 && response.status < 500) return { ok: false, reason: 'invalid' }
    return { ok: false, reason: 'server' }
  } catch {
    // 시간 초과와 네트워크 실패를 가르지 않는다 — 사용자가 할 일이 같다
    return { ok: false, reason: 'network' }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 문의 JSON 은 `inquiry` 파트에 문자열로 넣는다 — 서버가 그 파트를 **일반 폼 필드로 읽어
 * 직접 파싱한다** (`InquiryController.submitWithPhotos`). 필드를 하나씩 폼 필드로 흩으면
 * 계약이 JSON 과 폼 두 벌이 된다.
 */
function multipartBody(
  request: SubmitInquiryRequest,
  photos: readonly PreparedPhoto[],
): FormData {
  const form = new FormData()
  form.append('inquiry', JSON.stringify(prune(request)))
  for (const photo of photos) {
    form.append('photos', photo.blob, photo.name)
  }
  return form
}

/**
 * 빈 값을 빼고 보낸다. 브라우저·앱에 따라 못 읽는 값이 있고, 빈 문자열로 보내면 서버가
 * 그것을 값으로 저장한다.
 */
function prune(request: SubmitInquiryRequest): SubmitInquiryRequest {
  const entries = Object.entries(request).filter(([, value]) => {
    if (value === undefined || value === null) return false
    return typeof value !== 'string' || value.trim() !== ''
  })
  return Object.fromEntries(entries) as unknown as SubmitInquiryRequest
}
